import { Link, LinkProps, To } from 'react-router-dom'
import { useMemo } from 'react'
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu"
import { Menu, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useState, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { NavigationItem, MobileNavigationProps, NavigationSection } from "@/types/navigation"
import { navigationItems } from '@/types/navigation'

const NavigationLink = ({ children, to, ...props }: LinkProps & { to: To }) => {
  return <Link to={to} {...props}>{children}</Link>
}

export function MainNav() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const memoizedNavigationItems = useMemo(() => navigationItems, [])
  
  // Close menu when resizing to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsMenuOpen(false)
      }
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Prevent scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isMenuOpen])

  const DesktopNavigation = () => {
    const [activeItem, setActiveItem] = useState<NavigationItem | null>(null)

    const handleSectionHover = (section: NavigationSection) => {
      setActiveItem(section.items?.[0] || null)
    }

    return (
      <div className="hidden md:block">
        <NavigationMenu>
          <NavigationMenuList>
            {memoizedNavigationItems.map((section) => (
              <NavigationMenuItem
                key={section.title}
                onMouseEnter={() => handleSectionHover(section)}
              >
                <NavigationMenuTrigger>{section.title}</NavigationMenuTrigger>
                <NavigationMenuContent>
                  <div className="flex w-[800px] p-4">
                    <ul className="w-1/3 border-r pr-4">
                      {section.items.map((item) => (
                        <li key={item.title}>
                          <NavigationLink
                            to={item.href}
                            className={`block select-none rounded-md p-3 leading-none transition-colors cursor-pointer
                              ${activeItem === item
                                ? 'bg-neutral-100 dark:bg-neutral-800 text-black dark:text-white'
                                : 'hover:bg-neutral-50 dark:hover:bg-neutral-900 text-black dark:text-white'}`}
                            onMouseEnter={() => setActiveItem(item)}
                          >
                            {item.title}
                          </NavigationLink>
                        </li>
                      ))}
                    </ul>
                    <div className="w-2/3 pl-4">
                      {activeItem?.subItems && (
                        <ul className="grid grid-cols-2 gap-3">
                          {activeItem.subItems.map((subItem) => (
                            <li key={subItem.href}>
                              <NavigationLink
                                to={subItem.href}
                                className="block rounded-md p-2 hover:bg-neutral-50"
                              >
                                {subItem.label}
                              </NavigationLink>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </NavigationMenuContent>
              </NavigationMenuItem>
            ))}
          </NavigationMenuList>
        </NavigationMenu>
      </div>
    )
  }

  const MobileNavigation: React.FC<MobileNavigationProps> = ({ isOpen, onClose }) => {
    return (
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ type: "spring", bounce: 0, duration: 0.4 }}
            className="fixed inset-0 bg-white dark:bg-neutral-900 z-40 md:hidden overflow-y-auto"
          >
            <div className="pt-20 px-6 pb-8">
              {memoizedNavigationItems.map((item) => (
                <div key={item.title} className="mb-6">
                  <h2 className="text-lg font-semibold mb-3">{item.title}</h2>
                  {item.items && (
                    <div className="space-y-4 pl-4">
                      {item.items.map((subItem) => (
                        <div key={subItem.title}>
                          <NavigationLink
                            to={subItem.href}
                            className="block font-medium mb-2"
                            onClick={onClose}
                          >
                            {subItem.title}
                          </NavigationLink>
                          {subItem.subItems && (
                            <ul className="space-y-2 pl-4">
                              {subItem.subItems.map((subSubItem) => (
                                <li key={subSubItem.label}>
                                  <NavigationLink
                                    to={subSubItem.href}
                                    className="block text-sm text-neutral-600 dark:text-neutral-400"
                                    onClick={onClose}
                                  >
                                    {subSubItem.label}
                                  </NavigationLink>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    )
  }

  return (
    <div className="relative">
      {/* Mobile Menu Button */}
      <Button
        variant="ghost"
        size="icon"
        className="md:hidden fixed top-4 right-4 z-50"
        onClick={() => setIsMenuOpen(!isMenuOpen)}
      >
        {isMenuOpen ? (
          <X className="h-6 w-6" />
        ) : (
          <Menu className="h-6 w-6" />
        )}
      </Button>

      {/* Mobile Menu */}
      <MobileNavigation isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} />

      {/* Desktop Navigation */}
      <DesktopNavigation />
    </div>
  )
} 